<template>
  <div class="w-full">
    <div>
      <div class="flex flex-wrap items-center gap-3">
        <div>
          <p class="mb-0 leading-none text-2xl">{{ preTitle }}</p>
          <h1 class="mb-0 font-bold">{{ title }}</h1>
        </div>
        <div
          v-if="isDiscountedGame"
          class="px-3 py-1 font-extrabold rounded-md bg-danger text-mouse md:text-lg md:px-4 md:py-1.5"
        >
          {{ discountedPercentageText }}
        </div>
      </div>

      <div>
        <p class="m-0 font-bold">
          <landing-page-product-header-text
            v-if="startingPriceExists"
            :starting="starting"
            :is-discounted-game="isDiscountedGame"
            :discounted-percentage="discountedPercentage"
          />
        </p>

        <!-- order now button for xbox platform -->
        <common-button
          v-if="
            platform?.toLowerCase() === StrapiPlatform.XBox.toLowerCase() &&
            orderNowUrl
          "
          :href="orderNowUrl"
          size="lg"
          class="mt-2 min-w-40"
          :rel="microsoftStore ? ' noopener' : undefined"
          :target="microsoftStore ? '_blank' : undefined"
          data-track="landing-page-order-now"
          data-testid="hero-order-now"
        >
          {{
            preOrder ? $t('frontend/preorder-now') : $t('frontend/order-now')
          }}
        </common-button>

        <!-- order now button for Chargebee checkout (client-side navigation) -->
        <common-button
          v-else-if="chargebeePlanId && orderNowRoute"
          :to="orderNowRoute"
          size="lg"
          class="mt-2 min-w-40"
          data-track="landing-page-order-now"
          data-testid="hero-order-now"
        >
          {{
            preOrder ? $t('frontend/preorder-now') : $t('frontend/order-now')
          }}
        </common-button>

        <!-- order now button for all other products (legacy external link) -->
        <common-button
          v-else-if="!chargebeePlanId && orderNowUrl"
          :href="orderNowUrl"
          size="lg"
          class="mt-2 min-w-40"
          data-track="landing-page-order-now"
          data-testid="hero-order-now"
        >
          {{
            preOrder ? $t('frontend/preorder-now') : $t('frontend/order-now')
          }}
        </common-button>
      </div>

      <div ref="headerBar">
        <transition
          enter-active-class="transition-transform duration-300 ease-in-out"
          enter-from-class="-translate-y-full"
          leave-active-class="transition-transform duration-300 ease-in-out"
          leave-to-class="-translate-y-full"
        >
          <div
            v-if="isSticky"
            class="fixed left-0 right-0 z-99 top-18 xl:top-0 xl:left-80 bg-depth-0 py-3 border-b border-white/5"
          >
            <div
              class="flex items-start md:items-center max-w-screen-xl mx-auto px-4"
            >
              <template v-if="orderNowUrl">
                <common-button
                  v-if="chargebeePlanId && orderNowRoute"
                  :to="orderNowRoute"
                  size="lg"
                  class="mr-2 lg:mr-4"
                  data-track="landing-page-order-now"
                >
                  {{
                    preOrder
                      ? $t('frontend/preorder-now')
                      : $t('frontend/order-now')
                  }}
                </common-button>
                <common-button
                  v-else
                  :href="orderNowUrl"
                  size="lg"
                  class="mr-2 lg:mr-4"
                  :rel="microsoftStore ? ' noopener' : undefined"
                  :target="microsoftStore ? '_blank' : undefined"
                  data-track="landing-page-order-now"
                >
                  {{
                    preOrder
                      ? $t('frontend/preorder-now')
                      : $t('frontend/order-now')
                  }}
                </common-button>
              </template>
              <p class="mb-0 font-bold">
                {{ title }}
                <br />
                <landing-page-product-header-text
                  v-if="startingPriceExists"
                  :starting="starting"
                  :is-discounted-game="isDiscountedGame"
                  :discounted-percentage="discountedPercentage"
                />
              </p>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  StrapiPlatform,
  type StrapiProductStartConfiguration,
  StrapiProductType,
} from '~/apollo/types/types';
import { parseRouteUrl } from '~/helpers/checkout';
import { resolveHeaderOrderNowUrl } from '~/helpers/header-order-url';

const STICKY_THRESHOLD_PX = 16;

const props = withDefaults(
  defineProps<{
    title: string;
    preTitle?: string;
    defaultOrderUrl?: string | null;
    productKey?: string | null;
    productType: string;
    preOrder?: boolean;
    microsoftStore?: boolean;
    chargebeePlanId?: string | null;
    platform?: StrapiProductType | null;
    starting?: StrapiProductStartConfiguration | null;
    isDiscountedGame?: boolean;
    discountedPercentage?: number;
  }>(),
  {
    preTitle: '',
    defaultOrderUrl: null,
    productKey: null,
    preOrder: false,
    microsoftStore: false,
    chargebeePlanId: null,
    platform: null,
    starting: null,
    isDiscountedGame: false,
    discountedPercentage: 0.0,
  },
);

const locationStore = useLocationStore();
const selectedLocation = computed(() => locationStore.selectedLocation);

// Decision logic lives in ~/helpers/header-order-url so it can be
// unit-tested without mounting this component.
const orderNowUrl = computed(() =>
  resolveHeaderOrderNowUrl({
    microsoftStore: props.microsoftStore,
    defaultOrderUrl: props.defaultOrderUrl,
    chargebeePlanId: props.chargebeePlanId,
    selectedLocationOrderUrl: selectedLocation.value.orderUrl,
  }),
);

const orderNowRoute = computed(() => {
  const url = orderNowUrl.value;
  if (!url) return url;
  return parseRouteUrl(url);
});

const discountedPercentageText = computed<string>(
  () => `- ${Number(props.discountedPercentage)}%`,
);

const headerBar = ref<HTMLElement>();
const { y } = useScroll(import.meta.client ? window : undefined);
const isSticky = ref<boolean>(false);

watch(
  () => y.value,
  (newValue) => {
    if (headerBar.value == null) return;

    if (newValue >= headerBar.value.offsetTop + STICKY_THRESHOLD_PX) {
      isSticky.value = true;
    } else {
      isSticky.value = false;
    }
  },
);

onMounted(() => {
  if (y.value >= headerBar.value.offsetTop + STICKY_THRESHOLD_PX) {
    isSticky.value = true;
  }
});

const startingPriceExists = computed<boolean>(
  () => props.starting && props.starting.prices.length > 0,
);
</script>
